import type { Key } from 'react-aria';
import { newAccountTrialDurationDays } from '@gonfalon/dogfood-flags/dogfood-flags';
import { MarketoForm } from '@gonfalon/marketing-forms/forms';
import { isUnifiedFormSubmitEnabled } from '@gonfalon/marketing-forms/isUnifiedFormSubmitEnabled';
import { msg } from '@gonfalon/messages/msg';
import { SIGNUP_TAB_ENTERPRISE_DEMO, SIGNUP_TAB_TRIAL } from '@gonfalon/signup/analytics';
import { EnterpriseDemoSignupForm } from '@gonfalon/signup/EnterpriseDemoSignupForm';
import { EnterpriseDemoSignupFormLegacy } from '@gonfalon/signup/EnterpriseDemoSignupFormLegacy';
import { Tab, TabList, TabPanel, Tabs } from '@launchpad-ui/components';

import { OAuthButtons } from './OAuthButtons';
import { SignUpForm } from './SignUpForm';

import styles from './SignUpIntentTabs.module.css';

const TAB_TRIAL = SIGNUP_TAB_TRIAL;
const TAB_ENTERPRISE_DEMO = SIGNUP_TAB_ENTERPRISE_DEMO;

type SignUpIntentTabsProps = {
  selectedTab: string;
  onSelectedTabChange: (tab: string) => void;
  oauthRedirectPath?: string;
  /** Trial length quoted in the trial tab label and the form below it. */
  trialDurationDays?: number;
};

export const SignUpIntentTabs = ({
  selectedTab,
  onSelectedTabChange,
  oauthRedirectPath = '/signup',
  trialDurationDays = newAccountTrialDurationDays(),
}: SignUpIntentTabsProps) => {
  const handleTabChange = (key: Key) => {
    onSelectedTabChange(String(key));
  };

  const submitsThroughBackend = isUnifiedFormSubmitEnabled(MarketoForm.EnterpriseDemo);

  return (
    <Tabs selectedKey={selectedTab} onSelectionChange={handleTabChange} className={styles.tabs}>
      {/* TabList must be a direct child of Tabs (no wrapper) so Launchpad panels layout correctly. */}
      <TabList aria-label={msg('signUp.signUpIntentTabs.label')} className={styles.tabList}>
        <Tab id={TAB_TRIAL} className={styles.tab}>
          {msg('signUp.signUpIntentTabs.trial', { days: trialDurationDays })}
        </Tab>
        <Tab id={TAB_ENTERPRISE_DEMO} className={styles.tab}>
          {msg('signUp.signUpIntentTabs.enterpriseDemo')}
        </Tab>
      </TabList>
      <TabPanel id={TAB_TRIAL} className={styles.tabPanel}>
        <OAuthButtons oauthRedirectPath={oauthRedirectPath} />
        <section>
          <SignUpForm hideDemoLink trialDurationDays={trialDurationDays} />
        </section>
      </TabPanel>
      <TabPanel id={TAB_ENTERPRISE_DEMO} className={styles.tabPanel}>
        <section>{submitsThroughBackend ? <EnterpriseDemoSignupForm /> : <EnterpriseDemoSignupFormLegacy />}</section>
      </TabPanel>
    </Tabs>
  );
};
